import { db } from "../firebase/config";
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  collection, 
  addDoc,
  query, 
  where, 
  getDocs,
  serverTimestamp,
  Timestamp
} from "firebase/firestore";
import { ExamAssignment, AssessmentTemplate, Section, Question } from "../types";

export interface ExamSession {
  id: string;
  assignmentId: string;
  candidateId: string;
  examId: string;
  orgId: string;
  status: 'not_started' | 'in_progress' | 'paused' | 'completed' | 'submitted';
  startedAt: Date | null;
  pausedAt: Date | null;
  completedAt: Date | null;
  submittedAt: Date | null;
  timeSpent: number;
  currentSectionIndex: number;
  currentQuestionIndex: number;
  answers: Record<string, any>;
  sectionStatus: Record<string, {
    status: 'pending' | 'in_progress' | 'completed';
    startedAt: Date | null;
    completedAt: Date | null;
    timeSpent: number;
  }>;
  deviceInfo: {
    userAgent: string;
    screenResolution: string;
    ipAddress?: string;
  };
  lastActivityAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface ExamResponse {
  id: string;
  sessionId: string;
  assignmentId: string;
  candidateId: string;
  examId: string;
  orgId: string;
  sectionId: string;
  questionId: string;
  questionText: string;
  questionType: string;
  answer: any;
  traitId: string;
  traitName: string;
  isReverse: boolean;
  weightage: number;
  maxScale: number;
  rawScore: number;
  normalizedScore: number;
  timeSpentOnQuestion: number;
  submittedAt: Date;
  createdAt: Date;
}

class ExamSessionService {
  
  async createSession(assignmentId: string, candidateId: string): Promise<string> {
    try {
      const assignmentRef = doc(db, "exam_assignments", assignmentId);
      const assignmentSnap = await getDoc(assignmentRef);
      
      if (!assignmentSnap.exists()) {
        throw new Error("Assignment not found");
      }
      
      const assignment = assignmentSnap.data();
      
      const sessionId = `session_${Date.now()}_${candidateId}`;
      const sessionRef = doc(db, "exam_sessions", sessionId);
      
      const session: any = {
        id: sessionId,
        assignmentId,
        candidateId,
        examId: assignment.examId,
        orgId: assignment.orgId,
        status: 'not_started',
        startedAt: null,
        pausedAt: null,
        completedAt: null,
        submittedAt: null,
        timeSpent: 0,
        currentSectionIndex: 0,
        currentQuestionIndex: 0,
        answers: {},
        sectionStatus: {},
        deviceInfo: {
          userAgent: navigator.userAgent,
          screenResolution: `${window.screen.width}x${window.screen.height}`,
        },
        lastActivityAt: new Date(),
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await setDoc(sessionRef, session);
      return sessionId;
    } catch (error) {
      console.error("Error creating session:", error);
      throw error;
    }
  }
  
  async getSession(sessionId: string): Promise<ExamSession | null> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      const sessionSnap = await getDoc(sessionRef);
      
      if (!sessionSnap.exists()) {
        return null;
      }
      
      const data = sessionSnap.data();
      return {
        id: sessionSnap.id,
        assignmentId: data.assignmentId,
        candidateId: data.candidateId,
        examId: data.examId,
        orgId: data.orgId,
        status: data.status,
        startedAt: data.startedAt?.toDate() || null,
        pausedAt: data.pausedAt?.toDate() || null,
        completedAt: data.completedAt?.toDate() || null,
        submittedAt: data.submittedAt?.toDate() || null,
        timeSpent: data.timeSpent,
        currentSectionIndex: data.currentSectionIndex,
        currentQuestionIndex: data.currentQuestionIndex,
        answers: data.answers || {},
        sectionStatus: data.sectionStatus || {},
        deviceInfo: data.deviceInfo,
        lastActivityAt: data.lastActivityAt?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      };
    } catch (error) {
      console.error("Error getting session:", error);
      return null;
    }
  }
  
  async updateSessionProgress(
    sessionId: string,
    currentSectionIndex: number,
    currentQuestionIndex: number,
    timeSpent: number
  ): Promise<void> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      await updateDoc(sessionRef, {
        currentSectionIndex,
        currentQuestionIndex,
        timeSpent,
        lastActivityAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error("Error updating session progress:", error);
      throw error;
    }
  }
  
  async saveAnswer(
    sessionId: string,
    questionId: string,
    answer: any,
    sectionId: string,
    timeSpentOnQuestion: number
  ): Promise<void> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      const sessionSnap = await getDoc(sessionRef);
      
      if (!sessionSnap.exists()) {
        throw new Error("Session not found");
      }
      
      const currentAnswers = sessionSnap.data().answers || {};
      currentAnswers[questionId] = answer;
      
      await updateDoc(sessionRef, {
        answers: currentAnswers,
        lastActivityAt: new Date(),
        updatedAt: new Date()
      });
      
      const responseRef = collection(db, "exam_responses");
      await addDoc(responseRef, {
        sessionId,
        questionId,
        answer,
        sectionId,
        timeSpentOnQuestion,
        answeredAt: new Date()
      });
    } catch (error) {
      console.error("Error saving answer:", error);
      throw error;
    }
  }
  
  async updateSectionStatus(
    sessionId: string,
    sectionId: string,
    status: 'pending' | 'in_progress' | 'completed',
    timeSpent?: number
  ): Promise<void> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      const sessionSnap = await getDoc(sessionRef);
      
      if (!sessionSnap.exists()) {
        throw new Error("Session not found");
      }
      
      const currentSectionStatus = sessionSnap.data().sectionStatus || {};
      currentSectionStatus[sectionId] = {
        status,
        startedAt: status === 'in_progress' ? new Date() : (currentSectionStatus[sectionId]?.startedAt || null),
        completedAt: status === 'completed' ? new Date() : null,
        timeSpent: timeSpent || currentSectionStatus[sectionId]?.timeSpent || 0
      };
      
      await updateDoc(sessionRef, {
        sectionStatus: currentSectionStatus,
        updatedAt: new Date()
      });
    } catch (error) {
      console.error("Error updating section status:", error);
      throw error;
    }
  }
  
  async startExam(sessionId: string): Promise<void> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      await updateDoc(sessionRef, {
        status: 'in_progress',
        startedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error("Error starting exam:", error);
      throw error;
    }
  }
  
  async submitExam(sessionId: string, assignmentId: string, candidateId: string): Promise<void> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      await updateDoc(sessionRef, {
        status: 'submitted',
        submittedAt: new Date(),
        updatedAt: new Date()
      });
      
      const assignmentRef = doc(db, "exam_assignments", assignmentId);
      await updateDoc(assignmentRef, {
        status: 'completed',
        completedAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error("Error submitting exam:", error);
      throw error;
    }
  }
  
  async autoSaveProgress(sessionId: string, answers: Record<string, any>): Promise<void> {
    try {
      const sessionRef = doc(db, "exam_sessions", sessionId);
      await updateDoc(sessionRef, {
        answers,
        lastActivityAt: new Date(),
        updatedAt: new Date()
      });
    } catch (error) {
      console.error("Error auto-saving progress:", error);
    }
  }
  
  async getResponses(sessionId: string): Promise<ExamResponse[]> {
    try {
      const responsesRef = collection(db, "exam_responses");
      const q = query(responsesRef, where("sessionId", "==", sessionId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as ExamResponse));
    } catch (error) {
      console.error("Error getting responses:", error);
      return [];
    }
  }
}

export default new ExamSessionService();